import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useComponentTheme } from '@chegg-ui/theme';
import React, { useEffect, useRef, useState } from 'react';
import { transformDeprecatedSizeValues, useTabBehavior } from '../shared';
import { TabsContainer, TabUl, TabHeader, TabHeaderContainer, TabAffordance, getThemeConfig } from '../styles';

// adapted from https://inclusive-components.design/tabbed-interfaces/
var Tabs = function Tabs(_ref) {
  var id = _ref.id,
      tabs = _ref.tabs,
      activeTabIndex = _ref.activeTabIndex,
      onChangeIndex = _ref.onChangeIndex,
      ariaLabel = _ref['aria-label'],
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'lg' : _ref$size,
      _ref$orientation = _ref.orientation,
      orientation = _ref$orientation === void 0 ? 'horizontal' : _ref$orientation,
      className = _ref.className,
      theme = _ref.theme,
      dataTest = _ref['data-test'];
  var componentTheme = useComponentTheme({
    name: 'Tabs',
    theme: getThemeConfig(theme)
  });

  var _useTabBehavior = useTabBehavior(id, activeTabIndex, onChangeIndex, tabs, orientation),
      onTabKeyDown = _useTabBehavior.onTabKeyDown,
      onTabClick = _useTabBehavior.onTabClick;

  var activeOptionRef = useRef(null);
  var listRef = useRef(null);

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      listScrollLeft = _useState2[0],
      setListScrollLeft = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      listWidth = _useState4[0],
      setListWidth = _useState4[1];

  var _useState5 = useState(0),
      _useState6 = _slicedToArray(_useState5, 2),
      listScrollWidth = _useState6[0],
      setListScrollWidth = _useState6[1]; // This is for backward compatibility - need to remove this once everyone moved to new size props


  size = transformDeprecatedSizeValues(size); // whole effect only runs on mount

  useEffect(function () {
    // none of this logic applies to vertical orientation
    if (orientation === 'horizontal') {
      /* istanbul ignore else */
      if (activeOptionRef.current && listRef.current) {
        var activeTabElement = activeOptionRef.current;
        var listElement = listRef.current;
        var listElementRect = listElement.getBoundingClientRect();
        setListScrollLeft(listElement.scrollLeft);
        setListWidth(listElementRect.width);
        setListScrollWidth(listElement.scrollWidth); // nothing happens in the else case so no need to test it

        /* istanbul ignore else */

        if (listElementRect.width < listElement.scrollWidth) {
          // scroll the active tab into view immediately on mount
          listElement.scrollLeft = activeTabElement.getBoundingClientRect().left - listElementRect.width / 2 + listElementRect.x; // Rather then just using the onWheel prop, we need to setup a separate
          // listener to handle the wheel event, as onWheel is a passive event.
          // See https://medium.com/@Esakkimuthu/passive-event-listeners-5dbb1b011fb1

          /* istanbul ignore next */

          var onWheel = function onWheel(e) {
            var isTrackpad = false;

            if (e.deltaY) {
              if (e.deltaY === e.deltaY * -3) {
                isTrackpad = true;
              }
            } else if (e.deltaMode === 0) {
              isTrackpad = true;
            }

            if (!isTrackpad) {
              e.preventDefault();
              listElement.scrollLeft = listElement.scrollLeft + e.deltaY;
            }
          };

          listElement.addEventListener('wheel', onWheel);
          return function () {
            return listElement.removeEventListener('wheel', onWheel);
          };
        }
      }
    }
  }, []); // As the active tab changes scroll it into view, only applies to horizontal orientation

  useEffect(function () {
    /* istanbul ignore else */
    if (orientation === 'horizontal' && activeOptionRef.current && listRef.current && listRef.current.getBoundingClientRect().width < listRef.current.scrollWidth) {
      var list = listRef.current.getBoundingClientRect();
      var el = activeOptionRef.current.getBoundingClientRect();
      /* istanbul ignore else */

      if (el.left < list.left) {
        listRef.current.scrollLeft -= el.width + list.x;
      } else if (el.right > list.right) {
        listRef.current.scrollLeft += el.width + list.x;
      }
    }
  }, [activeTabIndex]);
  return /*#__PURE__*/React.createElement(TabsContainer, {
    className: className,
    "data-test": dataTest,
    orientation: orientation
  }, /*#__PURE__*/React.createElement(TabHeaderContainer, null, orientation === 'horizontal' && listScrollLeft > 0 && /*#__PURE__*/React.createElement(TabAffordance, {
    position: "left"
  }), /*#__PURE__*/React.createElement(TabUl, {
    id: id,
    role: "tablist",
    size: size,
    orientation: orientation,
    ref: listRef,
    "aria-orientation": orientation,
    onScroll: function onScroll(e) {
      return setListScrollLeft(e.target.scrollLeft);
    },
    "aria-label": ariaLabel,
    customTheme: componentTheme
  }, tabs.map(function (tab, index) {
    var isActive = index === activeTabIndex;
    return /*#__PURE__*/React.createElement("li", {
      role: "presentation",
      key: index,
      ref: isActive ? activeOptionRef : null
    }, /*#__PURE__*/React.createElement(TabHeader, {
      role: "tab",
      id: "".concat(id, "_tabheader_").concat(index),
      href: "#".concat(id, "_tabpanel_").concat(index),
      "aria-controls": "".concat(id, "_tabpanel_").concat(index),
      "data-index": index,
      onClick: onTabClick,
      onKeyDown: onTabKeyDown,
      tabIndex: isActive ? undefined : -1,
      "aria-selected": isActive ? true : undefined,
      size: size,
      orientation: orientation,
      customTheme: componentTheme
    }, tab.header));
  })), orientation === 'horizontal' && listScrollLeft < listScrollWidth - listWidth && /*#__PURE__*/React.createElement(TabAffordance, {
    position: "right"
  })), tabs.map(function (tab, index) {
    return /*#__PURE__*/React.createElement("section", {
      key: index,
      role: "tabpanel",
      "aria-labelledby": "".concat(id, "_tabheader_").concat(index),
      id: "".concat(id, "_tabpanel_").concat(index),
      hidden: index !== activeTabIndex,
      tabIndex: index === activeTabIndex ? 0 : -1
    }, tab.panel);
  }));
};

Tabs.displayName = 'Tabs';
export default Tabs;